import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { features } from "@/features/registry";

import type { PostHogSearch } from "..";
import { useGoals, useOverview, type FeatureGoal } from "../api";
import { GoalDrawer } from "./GoalDrawer";

/**
 * One row per *registry* feature — not per stored goal.
 *
 * A goal record only exists once someone states an intent, so driving the
 * table off the registry is what guarantees a feature nobody has thought
 * about yet still appears. Those are precisely the rows worth looking at.
 */
export interface GoalRow {
  featureKey: string;
  label: string;
  navGroup: string | null;
  goal: FeatureGoal | null;
  events: number;
  users: number;
}

export function GoalsTab({ days }: { days: number }) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/system/posthog" }) as PostHogSearch;
  const { data: goals, isLoading, error } = useGoals();
  // Best-effort — the goals table has to work before read-back is configured.
  const { data: overview } = useOverview(days);
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(null);
  const [query, setQuery] = useState("");

  const go = (next: Partial<PostHogSearch>) =>
    navigate({
      to: "/system/posthog",
      search: (prev: PostHogSearch) => ({ ...prev, ...next }),
    });

  const rows = useMemo<GoalRow[]>(() => {
    const byKey = new Map((goals ?? []).map((g) => [g.feature_key, g]));
    const usage = new Map((overview?.features ?? []).map((f) => [f.feature_key, f]));
    const all = features.map((f) => {
      const u = usage.get(f.key);
      return {
        featureKey: f.key,
        label: f.nav.label,
        navGroup: f.nav.group ?? null,
        goal: byKey.get(f.key) ?? null,
        events: u?.events ?? 0,
        users: u?.users ?? 0,
      };
    });

    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? all.filter(
          (r) =>
            r.label.toLowerCase().includes(needle) ||
            r.featureKey.toLowerCase().includes(needle) ||
            (r.navGroup ?? "").toLowerCase().includes(needle),
        )
      : all;

    const sorted = [...filtered];
    if (sort) {
      const dir = sort.dir === "asc" ? 1 : -1;
      sorted.sort((a, b) => {
        switch (sort.key) {
          case "events":
            return (a.events - b.events) * dir;
          case "users":
            return (a.users - b.users) * dir;
          case "purpose":
            return ((a.goal?.purpose ? 1 : 0) - (b.goal?.purpose ? 1 : 0)) * dir;
          case "navGroup":
            return (a.navGroup ?? "").localeCompare(b.navGroup ?? "") * dir;
          default:
            return a.label.localeCompare(b.label) * dir;
        }
      });
    } else {
      // Default: features still missing a purpose first — that's the to-do list.
      sorted.sort(
        (a, b) =>
          (a.goal?.purpose ? 1 : 0) - (b.goal?.purpose ? 1 : 0) ||
          b.events - a.events ||
          a.label.localeCompare(b.label),
      );
    }
    return sorted;
  }, [goals, overview, sort, query]);

  const columns: DataTableColumn<GoalRow>[] = [
    {
      key: "label",
      header: "Feature",
      sortable: true,
      render: (r) => <span className="font-medium">{r.label}</span>,
    },
    {
      key: "navGroup",
      header: "Group",
      sortable: true,
      render: (r) => (
        <span className="text-muted-foreground">{r.navGroup ?? "—"}</span>
      ),
    },
    {
      key: "purpose",
      header: "Purpose",
      sortable: true,
      render: (r) =>
        r.goal?.purpose ? (
          <StatusBadge value="complete" label="Set" />
        ) : (
          <StatusBadge value="pending" label="Not set" />
        ),
    },
    {
      key: "target",
      header: "Target",
      align: "right",
      numeric: true,
      render: (r) => {
        const g = r.goal;
        if (!g || (g.target_events == null && g.target_active_users == null)) {
          return <span className="text-muted-foreground">—</span>;
        }
        const parts: string[] = [];
        if (g.target_active_users != null) parts.push(`${g.target_active_users} people`);
        if (g.target_events != null) parts.push(`${g.target_events} opens`);
        return (
          <span className="whitespace-nowrap">
            {parts.join(" · ")}
            <span className="ml-1 text-xs text-muted-foreground">/{g.cadence}</span>
          </span>
        );
      },
    },
    {
      key: "users",
      header: "People",
      align: "right",
      numeric: true,
      sortable: true,
      render: (r) => <span className="tabular-nums">{r.users}</span>,
    },
    {
      key: "events",
      header: "Opens",
      align: "right",
      numeric: true,
      sortable: true,
      render: (r) => <span className="tabular-nums">{r.events.toLocaleString()}</span>,
    },
  ];

  const withPurpose = rows.filter((r) => r.goal?.purpose).length;
  const selected = search.feature
    ? (rows.find((r) => r.featureKey === search.feature) ?? null)
    : null;

  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        A goal says what a feature is <em>for</em> and what &ldquo;used enough&rdquo;
        looks like. The weekly review compares actual usage against it — without a
        stated purpose it can only report that a number moved, not whether that
        matters. {withPurpose} of {rows.length} features have one.
      </p>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.featureKey}
        sort={sort}
        onSortChange={setSort}
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search features…"
        loading={isLoading}
        error={error instanceof Error ? error.message : null}
        emptyMessage="No features match."
        onRowClick={(r) => go({ feature: r.featureKey })}
        selectedKey={search.feature ?? null}
      />

      <GoalDrawer
        row={selected}
        open={selected != null}
        expanded={search.expanded === true}
        onOpenChange={(v) => {
          if (!v && !search.expanded) go({ feature: undefined, expanded: undefined });
        }}
        onExpandedChange={(exp) => go({ expanded: exp ? true : undefined })}
        onClose={() => go({ feature: undefined, expanded: undefined })}
      />
    </div>
  );
}
